import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const COLORS = {
  primary: "#0760ad",
  secondary: "#118a94",
  white: "#FFFFFF"
};

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const AppointmentManagement = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [filters, setFilters] = useState({
    doctor_id: '',
    status: 'all',
    date: ''
  });
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const apiUrl = import.meta.env.VITE_API_URL;

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${apiUrl}/doctors/`);
      setDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.doctor_id) params.append('doctor_id', filters.doctor_id);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.date) params.append('date', filters.date);

      const response = await axios.get(`${apiUrl}/appointments/?${params.toString()}`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoctors();
    fetchAppointments();
  }, [filters]);

  const getDoctorById = (id) => doctors.find(doc => doc.id === id);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await axios.patch(`${apiUrl}/appointments/${appointmentId}/`, { status: newStatus });
      fetchAppointments();
    } catch (error) {
      console.error('Error updating appointment status:', error);
    }
  };

  const exportToExcel = () => {
    const dataToExport = appointments.map(appointment => {
      const doctor = getDoctorById(appointment.doctor);
      return {
        'Appointment ID': appointment.id,
        'Patient Name': appointment.patient_name,
        'Patient Email': appointment.patient_email,
        'Patient Phone': appointment.patient_phone,
        'Doctor': doctor ? doctor.name : 'N/A',
        'Date': appointment.appointment_date,
        'Time': appointment.appointment_time,
        'Status': appointment.status,
        'Reason': appointment.reason,
        'Created At': appointment.created_at
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Appointments');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, 'appointments_export.xlsx');
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-2xl font-bold mb-4 md:mb-0" style={{ color: COLORS.primary }}>
          Appointment Management
        </h2>
        <button
          onClick={exportToExcel}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
        >
          Export to Excel
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Doctor</label>
          <select
            name="doctor_id"
            value={filters.doctor_id}
            onChange={handleFilterChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0760ad]"
          >
            <option value="">All Doctors</option>
            {doctors.map(doc => (
              <option key={doc.id} value={doc.id}>Dr. {doc.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0760ad]"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Date</label>
          <input
            type="date"
            name="date"
            value={filters.date}
            onChange={handleFilterChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0760ad]"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0760ad]"></div>
        </div>
      ) : appointments.length === 0 ? (
        <div className="bg-gray-50 p-8 rounded-lg text-center">
          <p className="text-gray-600">No appointments found.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Patient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Doctor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Date & Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {appointments.map(appointment => {
                const doctor = getDoctorById(appointment.doctor);
                return (
                  <tr key={appointment.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{appointment.patient_name}</div>
                      <div className="text-sm text-gray-500">{appointment.patient_email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">Dr. {doctor?.name || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{doctor?.specialty?.map(s => s.name).join(', ') || 'No specialties'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div>{formatDate(appointment.appointment_date)}</div>
                      <div className="text-sm text-gray-500">{formatTime(appointment.appointment_time)}</div>
                    </td>
                    
                    <td className="px-6 py-4 text-sm">
                      <div className="flex space-x-2">
                        <button onClick={() => { setSelectedAppointment(appointment); setShowDetailsModal(true); }} className="text-[#0760ad]">View</button>
                        
                        
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedAppointment && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between mb-4">
                <h2 className="text-xl font-bold text-[#0760ad]">Appointment Details</h2>
                <button onClick={() => setShowDetailsModal(false)} className="text-gray-500 hover:text-gray-700">&times;</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Patient Name</p>
                  <p className="font-medium">{selectedAppointment.patient_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Patient Email</p>
                  <p className="font-medium">{selectedAppointment.patient_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium">{selectedAppointment.patient_phone}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Doctor</p>
                  <p className="font-medium">Dr. {getDoctorById(selectedAppointment.doctor)?.name || 'N/A'}</p>
                  <p className="text-sm">{getDoctorById(selectedAppointment.doctor)?.specialty?.map(s => s.name).join(', ') || 'No specialties'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date & Time</p>
                  <p className="font-medium">{formatDate(selectedAppointment.appointment_date)} at {formatTime(selectedAppointment.appointment_time)}</p>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-gray-600">Reason</p>
                <p className="font-medium">{selectedAppointment.reason || 'N/A'}</p>
              </div>
              <div className="mt-6 text-right">
                <button onClick={() => setShowDetailsModal(false)} className="px-6 py-2 bg-[#0760ad] text-white rounded hover:bg-[#118a94]">Close</button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AppointmentManagement;
